/* Yatas Bedding NL — header behaviour.
 *
 * Mirrors the reference site:
 *   - rotating promo stripe
 *   - header hides on scroll-down, returns on scroll-up
 *   - transparent -> solid once scrolled (homepage only; the attribute decides)
 *   - menu tabs open a full-height left drawer with a category rail
 */
(function () {
  'use strict';

  const SOLID_AT = 10; // px scrolled before the transparent header fills in

  class YbnHeader extends HTMLElement {
    connectedCallback() {
      this.root = document.querySelector('[data-menu-root]');
      this.syncHeight();
      this.startPromoRotation();
      this.wireScroll();
      this.wireMenu();
      this.wireMobileSearch();
    }

    /* Publish the real header height so .ybn-header-spacer can reserve it.
       Horizon's own --header-height is only set for <header-component>, so
       without this the spacer fell back to 70px under a ~110px bar and the top
       of every non-home page rendered underneath the header. */
    syncHeight() {
      const apply = () => {
        const h = Math.round(this.getBoundingClientRect().height);
        if (h > 0) document.documentElement.style.setProperty('--ybn-header-h', `${h}px`);
      };
      apply();

      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(apply);
        this._ro.observe(this);
      } else {
        this._onResize = apply;
        window.addEventListener('resize', this._onResize);
      }

      // webfonts land after first paint and change the bar's height
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
    }

    disconnectedCallback() {
      if (this._promoTimer) clearInterval(this._promoTimer);
      if (this._onScroll) (this._scroller || window).removeEventListener('scroll', this._onScroll);
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      if (this._ro) this._ro.disconnect();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    /* ---------------------------------------------------------------- promo */
    startPromoRotation() {
      const stripe = this.querySelector('.ybn-band__stripe');
      if (!stripe) return;

      const promos = Array.from(stripe.querySelectorAll('.ybn-band__promo'));
      if (promos.length < 2) return;

      const interval = parseInt(stripe.dataset.rotateMs, 10) || 5000;
      let i = 0;

      this._promoTimer = setInterval(() => {
        promos[i].classList.remove('is-active');
        i = (i + 1) % promos.length;
        promos[i].classList.add('is-active');
      }, interval);
    }

    /* --------------------------------------------------------------- scroll */
    /* This theme scrolls an inner element, not the window: html and body are
       overflow:hidden and .page-wrapper carries the scrollbar. window.scrollY is
       therefore permanently 0, so a window listener never fires and .is-solid is
       never set. Find the real scroller and listen to that instead. */
    getScroller() {
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 4) return wrapper;
      let el = this.parentElement;
      while (el && el !== document.body) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 4) return el;
        el = el.parentElement;
      }
      return window;
    }

    wireScroll() {
      if (this.dataset.sticky !== 'true') return;

      let ticking = false;
      this._scroller = this.getScroller();
      const readY = () => (this._scroller === window
        ? window.scrollY || document.documentElement.scrollTop || 0
        : this._scroller.scrollTop);

      const apply = () => {
        const y = readY();

        // Fill the transparent header in as soon as the page moves. The header
        // then stays pinned -- solid bar plus announcement marquee -- exactly as
        // on the reference. It deliberately does NOT hide on scroll-down: that
        // would pull the announcement bar back out of view the moment it appears.
        this.classList.toggle('is-solid', y > SOLID_AT);
        this.classList.remove('is-hidden');

        ticking = false;
      };

      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(apply);
      };

      this._scroller.addEventListener('scroll', this._onScroll, { passive: true });
      // the inner scroller can be created after the header upgrades, so re-check
      // once the page has settled and move the listener if it changed
      setTimeout(() => {
        const now = this.getScroller();
        if (now !== this._scroller) {
          this._scroller.removeEventListener('scroll', this._onScroll);
          this._scroller = now;
          this._scroller.addEventListener('scroll', this._onScroll, { passive: true });
        }
        apply();
      }, 1200);
      apply();
    }

    /* ----------------------------------------------------------- mobile UI */
    wireMobileSearch() {
      const toggle = this.querySelector('.ybn-header__search-toggle');
      const form = this.querySelector('.ybn-search');
      if (!toggle || !form) return;

      toggle.addEventListener('click', () => {
        const showing = form.style.display === 'flex';
        form.style.display = showing ? '' : 'flex';
        if (!showing) form.querySelector('.ybn-search__input')?.focus();
      });
    }

    /* ----------------------------------------------------------------- menu */
    wireMenu() {
      if (!this.root) return;

      // Openers: the burger (no value -> first group) and each tab button.
      this.querySelectorAll('[data-open-menu]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openMenu(btn.getAttribute('data-open-menu') || null, btn);
        });
      });

      this.root.querySelectorAll('[data-close-menu]').forEach((el) => {
        el.addEventListener('click', () => this.closeMenu());
      });

      // Category rail -> swap the visible pane. On mobile this is also the
      // first step of the drill-down, so the drawer moves to level 1.
      const activatePane = (cat) => {
        const group = cat.closest('.ybn-mm__group');
        if (!group) return;
        group.querySelectorAll('.ybn-mm__cat').forEach((c) => c.classList.remove('is-active'));
        cat.classList.add('is-active');

        const id = cat.getAttribute('data-pane');
        group.querySelectorAll('.ybn-mm__pane').forEach((p) => {
          p.hidden = p.getAttribute('data-pane-id') !== id;
          p.classList.remove('is-sub');
        });
        const content = group.querySelector('.ybn-mm__content');
        if (content) content.scrollTop = 0;
      };

      this.root.querySelectorAll('.ybn-mm__cat').forEach((cat) => {
        cat.addEventListener('click', (e) => {
          // The rail is a link now, so on desktop the click opens the
          // category's own page. On mobile the same control is the first step
          // of the drill-down, so the navigation is swallowed there instead.
          if (this.isMobile()) e.preventDefault();
          activatePane(cat);
          this.setLevel(1);
        });

        // Desktop can still preview a panel without leaving the page.
        cat.addEventListener('mouseenter', () => {
          if (!this.isMobile()) activatePane(cat);
        });
      });

      // Sub-group. Desktop expands the leaves inline; mobile drills into them.
      this.root.querySelectorAll('.ybn-mm__group-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (this.isMobile()) {
            const pane = btn.closest('.ybn-mm__pane');
            const item = btn.closest('.ybn-mm__group-item');
            if (!pane || !item) return;

            pane.querySelectorAll('.ybn-mm__group-item').forEach((g) => g.classList.remove('is-active'));
            item.classList.add('is-active');

            const list = btn.nextElementSibling;
            if (list && list.classList.contains('ybn-mm__leaves')) list.hidden = false;

            const head = pane.querySelector('.ybn-mm__subhead');
            if (head) {
              head.hidden = false;
              const t = head.querySelector('[data-sub-title]');
              const a = head.querySelector('[data-sub-all]');
              const label = btn.querySelector('span');
              if (t && label) t.textContent = label.textContent.trim();
              // "Alle producten" should point at the sub-group itself.
              const own = list ? list.querySelector('.ybn-mm__leaf-all') : null;
              if (a && own) a.setAttribute('href', own.getAttribute('href'));
            }
            pane.classList.add('is-sub');
            this.setLevel(2);
            return;
          }

          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          const list = btn.nextElementSibling;
          if (list && list.classList.contains('ybn-mm__leaves')) list.hidden = open;
        });
      });

      // Mobile tab strip inside the drawer.
      this.root.querySelectorAll('[data-mobile-tab]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-mobile-tab');
          this.root.querySelectorAll('[data-mobile-tab]').forEach((b) => {
            b.classList.toggle('is-active', b === btn);
          });
          this.root.querySelectorAll('[data-tab-group]').forEach((g) => {
            g.hidden = g.getAttribute('data-tab-group') !== id;
          });
          this.setLevel(0);
        });
      });

      // Back buttons.
      this.root.querySelectorAll('[data-mm-back]').forEach((b) => {
        b.addEventListener('click', () => this.setLevel(0));
      });

      this.root.querySelectorAll('[data-mm-subback]').forEach((b) => {
        b.addEventListener('click', () => {
          const pane = b.closest('.ybn-mm__pane');
          if (pane) {
            pane.classList.remove('is-sub');
            const head = pane.querySelector('.ybn-mm__subhead');
            if (head) head.hidden = true;
            pane.querySelectorAll('.ybn-mm__leaves').forEach((l) => (l.hidden = true));
            pane.querySelectorAll('.ybn-mm__group-item').forEach((g) => g.classList.remove('is-active'));
          }
          this.setLevel(1);
        });
      });

      this._onKey = (e) => {
        if (e.key === 'Escape' && this._open) this.closeMenu();
      };
      document.addEventListener('keydown', this._onKey);
    }

    /* The drawer is a two-column panel on desktop and a drill-down on mobile;
       the breakpoint matches the CSS. */
    isMobile() {
      return window.matchMedia('(max-width: 1199px)').matches;
    }

    setLevel(n) {
      if (!this.root) return;
      this.root.classList.remove('is-level-0', 'is-level-1', 'is-level-2');
      this.root.classList.add('is-level-' + n);
    }

    openMenu(tabId, trigger) {
      const groups = Array.from(this.root.querySelectorAll('[data-tab-group]'));
      if (!groups.length) return;

      const target =
        (tabId && groups.find((g) => g.getAttribute('data-tab-group') === tabId)) || groups[0];
      groups.forEach((g) => {
        g.hidden = g !== target;
      });

      // Keep the mobile tab strip in step with whichever tab was opened.
      this.root.querySelectorAll('[data-mobile-tab]').forEach((b) => {
        b.classList.toggle('is-active', b.getAttribute('data-mobile-tab') === target.getAttribute('data-tab-group'));
      });
      this.setLevel(0);

      this.root.hidden = false;
      // Force a frame so the transform transition actually runs.
      window.requestAnimationFrame(() => this.root.classList.add('is-open'));

      this._open = true;
      this._trigger = trigger || null;
      this.classList.remove('is-hidden');
      this.classList.add('is-solid');
      document.body.style.overflow = 'hidden';

      this.querySelectorAll('.ybn-nav__tab-link[aria-expanded]').forEach((b) => {
        b.setAttribute('aria-expanded', String(b === trigger));
      });
    }

    closeMenu() {
      if (!this.root || !this._open) return;

      this.root.classList.remove('is-open');
      this._open = false;
      document.body.style.overflow = '';

      this.querySelectorAll('.ybn-nav__tab-link[aria-expanded]').forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
      });

      if (window.scrollY <= SOLID_AT) this.classList.remove('is-solid');
      if (this._trigger && typeof this._trigger.focus === 'function') this._trigger.focus();

      const done = () => {
        if (!this._open) this.root.hidden = true;
        this.root.removeEventListener('transitionend', done);
      };
      this.root.addEventListener('transitionend', done);
      // Fallback in case the transition never fires (reduced motion, etc.).
      setTimeout(() => {
        if (!this._open) this.root.hidden = true;
      }, 400);
    }
  }

  if (!customElements.get('ybn-header')) {
    customElements.define('ybn-header', YbnHeader);
  }
})();
